import type { OnboardingData } from "./types";
import type { UpdatePatientInput } from "@src/packages/patients/update-patient-request";

type PatientFertilityIntent = NonNullable<
  UpdatePatientInput["fertilityIntent"]
>;

/**
 * The generated PATCH type still requires periodRegularity inside an intent
 * object. Keep all other fields API-derived while allowing the compatibility
 * payload to omit the unasked field until the backend schema is relaxed.
 */
type OnboardingPatientPayload = Omit<UpdatePatientInput, "fertilityIntent"> & {
  fertilityIntent?: Partial<PatientFertilityIntent>;
};

/**
 * Build the patient PATCH without inventing clinical answers.
 *
 * The deployed legacy PATCH currently models periodRegularity as required,
 * even though onboarding does not ask it. Keep the known fertility fields in
 * the payload, but only add periodRegularity when a future explicit screen
 * has populated it. The API schema must be relaxed before this request can be
 * represented without the compatibility cast at the network boundary.
 */
export function buildOnboardingPatientPayload(
  dataToSave: OnboardingData,
  stytchPhone: string | undefined,
): OnboardingPatientPayload {
  const patientPayload: OnboardingPatientPayload = {};

  if (dataToSave.firstName) patientPayload.firstName = dataToSave.firstName;
  if (dataToSave.gender) patientPayload.gender = dataToSave.gender;

  if (dataToSave.dateOfBirth) {
    patientPayload.dateOfBirth = dataToSave.dateOfBirth;
  } else if (dataToSave.age) {
    const currentYear = new Date().getFullYear();
    const approxYear = currentYear - dataToSave.age;
    patientPayload.dateOfBirth = `${approxYear}-01-01`;
  }

  if (dataToSave.ethnicity.length > 0) {
    patientPayload.ethnicity = dataToSave.ethnicity.join(", ");
  }

  if (dataToSave.partnerInvite) {
    patientPayload.partnerChoice = "with-partner";
  } else if (dataToSave.partnerChoice) {
    patientPayload.partnerChoice = dataToSave.partnerChoice;
  }

  if (dataToSave.timeline) patientPayload.timeline = dataToSave.timeline;

  if (dataToSave.zipCode || dataToSave.country) {
    patientPayload.addresses = [
      {
        ...(dataToSave.zipCode ? { postalCode: dataToSave.zipCode } : {}),
        ...(dataToSave.country ? { country: dataToSave.country } : {}),
      },
    ];
  }

  if (stytchPhone) patientPayload.phoneNumber = stytchPhone;

  const fertilityIntent: Partial<PatientFertilityIntent> = {};
  let hasFertilityData = false;

  if (dataToSave.treatmentIntent) {
    const goalMap: Record<
      string,
      Exclude<PatientFertilityIntent["goal"], null>
    > = {
      exploring: "exploring",
      preserve: "egg-preserve",
      "egg-freezing": "egg-freezing",
      "embryo-preserve": "embryo-preserve",
      "sperm-preserve": "sperm-preserve",
      pregnancy: "pregnancy",
      "egg-donation": "egg-donation",
      "sperm-donation": "sperm-donation",
      adoption: "adoption",
      surrogacy: "surrogacy",
      Exploring: "exploring",
      Preservation: "egg-preserve",
      Donation: "egg-donation",
      Conception: "pregnancy",
      StartingTreatment: "pregnancy",
      InTreatment: "pregnancy",
    };
    const goal = goalMap[dataToSave.treatmentIntent];
    if (goal) {
      fertilityIntent.goal = goal;
      hasFertilityData = true;
    }
  }

  if (dataToSave.primaryIntent) {
    fertilityIntent.primaryIntent = dataToSave.primaryIntent;
    hasFertilityData = true;
  }

  if (dataToSave.journeyRole) {
    fertilityIntent.journeyRole = dataToSave.journeyRole;
    hasFertilityData = true;
  }

  if (dataToSave.fertilityStage) {
    fertilityIntent.stage = dataToSave.fertilityStage;
    hasFertilityData = true;
  }

  if (dataToSave.plannedTreatmentModality) {
    fertilityIntent.plannedModalities = [dataToSave.plannedTreatmentModality];
    hasFertilityData = true;
  }

  if (dataToSave.currentTreatmentFamily) {
    fertilityIntent.currentTreatmentFamily = dataToSave.currentTreatmentFamily;
    hasFertilityData = true;
  }

  if (dataToSave.hasEmployerBenefits !== null) {
    fertilityIntent.hasEmployerBenefits = dataToSave.hasEmployerBenefits;
    fertilityIntent.employerBenefit = dataToSave.hasEmployerBenefits
      ? dataToSave.employerBenefit
      : null;
    hasFertilityData = true;
  }

  if (dataToSave.periodRegularity) {
    fertilityIntent.periodRegularity = dataToSave.periodRegularity;
    hasFertilityData = true;
  }

  // The first persisted completion timestamp is the server-side signup event.
  // The API uses the existing fertility-intent row to make the notification
  // idempotent when this save is retried.
  fertilityIntent.completedAt = new Date().toISOString();
  hasFertilityData = true;

  if (hasFertilityData) patientPayload.fertilityIntent = fertilityIntent;

  return patientPayload;
}
